import Bugsnag, { type Event } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import config from 'config';
import { noop } from 'lodash';
import type React from 'react';

import { isFeatureEnabled } from './feature';
import { blurSensitiveData } from './filter';

const EmptyErrorBoundary: React.FC<{ children?: React.ReactNode }> = (props) => (
	<>{props.children}</>
);

let bugsnagClient: any = {
	getPlugin: () => ({ createErrorBoundary: () => EmptyErrorBoundary }),
	leaveBreadcrumb: noop,
	notify: noop,
	user: { id: null },
};

// Intentional fetch cancellations reject with an AbortError ("signal is aborted without
// reason"). When we abort an in-flight request (e.g. SearchPage cancelling a search on
// filter/tab change), Chromium surfaces the response body stream's internal cancellation
// as an unhandled rejection that the per-request `.catch()` can't intercept, so
// autoDetectErrors reports it. These are expected cancellations, not bugs — discard them.
const isAbortError = (error: unknown): boolean =>
	!!error && typeof error === 'object' && (error as { name?: string }).name === 'AbortError';

// An empty key is how a preview build turns Bugsnag off (`BUGSNAG_DISABLED` in
// vite.config.ts) — the stub client above stays in place, so no preview event
// ever reaches the Bugsnag project.
if (isFeatureEnabled('bugsnag') && BUGSNAG_API_KEY !== '') {
	bugsnagClient = Bugsnag.start({
		apiKey: BUGSNAG_API_KEY,
		autoDetectErrors: config.bugsnag.notify,
		enabledReleaseStages: ['production', 'staging'],
		releaseStage: config.bugsnag.stage,
		plugins: [new BugsnagPluginReact()],
		appVersion: APP_VERSION,
		onError: (event) => {
			if (
				isAbortError(event.originalError) ||
				event.errors.some((e) => e.errorClass === 'AbortError')
			) {
				return false;
			}
			return true;
		},
	});
}

export function notifyBugsnagNetwork(service: string, method: string, args: any = null) {
	if (!isFeatureEnabled('bugsnag')) return;

	const blurredArgs = blurSensitiveData(args);
	bugsnagClient.leaveBreadcrumb('Network Request', {
		args: JSON.stringify(blurredArgs, null, 2),
		method,
		service,
	});
}

/** Extra Bugsnag metadata, keyed by the tab (section) it renders under. */
export type BugsnagMetadata = Record<string, Record<string, unknown>>;

const addMetadata = (event: Event, metadata?: BugsnagMetadata) => {
	if (!metadata) return;
	for (const [section, values] of Object.entries(metadata)) {
		event.addMetadata(section, values);
	}
};

export function notifyBugsnagHandledError(err: Error, metadata?: BugsnagMetadata) {
	if (!isFeatureEnabled('bugsnag')) return;

	bugsnagClient.notify(err, (event: Event) => addMetadata(event, metadata));
}

/**
 * Reports a non-error diagnostic (e.g. a recovery after a failure) at info
 * severity, so it can be counted next to the failure events it belongs to.
 */
export function notifyBugsnagInfo(message: string, metadata?: BugsnagMetadata) {
	if (!isFeatureEnabled('bugsnag')) return;

	bugsnagClient.notify(new Error(message), (event: Event) => {
		event.severity = 'info';
		addMetadata(event, metadata);
	});
}

export default bugsnagClient;
